<script>
import { DateTime, Duration, Info } from 'luxon'
import PlanningLineComponent from './PlanningLine.vue'
import { range } from 'lodash'

export default {
    name: 'Planning',
    components: {
        PlanningLineComponent,
    },
    props: {
        initTimetable: {
            type: Array,
            default() {
                return []
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        inputName: {
            type: String,
            default: 'planning_lines',
        },
        schedules: {
            type: Array,
            default() {
                return []
            },
        },
    },
    data() {
        return {
            tempBatch: this.initialBatchLine(),
            timetable: [],
            schedule: null,
            timeSlot: 'continuous',
            dateError: '',
        }
    },
    computed: {
        formIsDisabled() {
            return 0 === Object.values(this.tempBatch.weekdays).filter(value => value).length
        },
        selectedLines() {
            return this.timetable.filter(value => {
                return value.selected
            })
        },
        planningDuration() {
            const duration = this.timetable
                .reduce(function (carry, line) {
                    return carry.plus(line.to.diff(line.from, ['hours', 'minutes']))
                }, Duration.fromObject({}))
                .shiftTo('hours', 'minutes')
                .toObject()
            return {
                hours: duration.hours,
                minutes: String(duration.minutes).padStart(2, '0'),
            }
        },
        planningDurationPerWeek() {
            return this.timetable.reduce((carry, line) => {
                this.splitLineByWeek(line).forEach(({ weekNumber, duration }) => {
                    carry[weekNumber] = (weekNumber in carry ? carry[weekNumber].plus(duration) : duration).shiftTo(
                        'hours',
                        'minutes'
                    )
                })

                return carry
            }, {})
        },

        mergedTimetable() {
            return this.timetable.reduce((merged, line) => {
                const previous = merged[merged.length - 1]
                const isWeekBoundary = +line.from === +line.from.startOf('week')

                if (previous && isWeekBoundary && +previous.to === +line.from) {
                    previous.to = line.to
                } else {
                    merged.push({ from: line.from, to: line.to, selected: line.selected })
                }

                return merged
            }, [])
        },
        weekdays() {
            return range(1, 8).reduce((acc, element) => {
                return {
                    ...acc,
                    [element]: Info.weekdays('long')[element - 1],
                }
            }, {})
        },
    },
    watch: {},
    created() {
        this.timetable = this.initTimetable.flatMap(line =>
            this.splitAtWeekBoundary(DateTime.fromISO(line.from), DateTime.fromISO(line.to)).map(segment => ({
                selected: false,
                from: segment.from,
                to: segment.to,
            }))
        )
    },
    methods: {
        addLineFromBatch(date, fromTime, toTime) {
            const endDate = fromTime > toTime ? date.plus({ days: 1 }) : date
            fromTime = fromTime.split(':')
            toTime = toTime.split(':')

            this.addToTimetable({
                from: date.set({
                    hours: fromTime[0],
                    minutes: fromTime[1],
                }),
                to: endDate.set({
                    hours: toTime[0],
                    minutes: toTime[1],
                }),
            })
        },
        addToTimetable(line) {
            this.splitAtWeekBoundary(line.from, line.to).forEach(segment => {
                this.timetable.push({
                    from: segment.from,
                    to: segment.to,
                    selected: false,
                })
            })

            this.sortTimetable()
        },
        batchAddLines() {
            let startDate = DateTime.fromISO(this.tempBatch.from)
            const endDate = DateTime.fromISO(this.tempBatch.to)
            const now = DateTime.now()
            const minDate = now.minus({ years: 1 })
            const maxDate = now.plus({ years: 1 })

            if (startDate < minDate || startDate > maxDate) {
                this.dateError = `* La date de début n'est pas valide.`
                return
            }

            this.dateError = ''

            do {
                if (this.tempBatch.weekdays[startDate.weekday]) {
                    this.addLineFromBatch(startDate, this.tempBatch.beginAtAM, this.tempBatch.endAtAM)

                    if (null !== this.tempBatch.beginAtPM && null !== this.tempBatch.endAtPM) {
                        this.addLineFromBatch(startDate, this.tempBatch.beginAtPM, this.tempBatch.endAtPM)
                    }
                }

                startDate = startDate.plus({ day: 1 })
            } while (startDate <= endDate)

            this.resetBatchLine()
        },
        dropLine(index) {
            this.timetable.splice(index, 1)

            this.sortTimetable()
        },
        dropAllLines() {
            this.timetable.splice(0)

            this.sortTimetable()
        },
        duplicateToNextDay(index) {
            const initialLine = this.timetable[index]

            this.addToTimetable({
                from: initialLine.from.plus({ day: 1 }),
                to: initialLine.to.plus({ day: 1 }),
            })
        },
        getInputName() {
            return this.inputName
        },
        initialBatchLine() {
            return {
                from: null,
                to: null,
                beginAtAM: null,
                endAtAM: null,
                beginAtPM: null,
                endAtPM: null,
                weekdays: {
                    1: true,
                    2: true,
                    3: true,
                    4: true,
                    5: true,
                    6: true,
                    7: true,
                },
            }
        },
        onLineUpdated() {
            this.sortTimetable()
        },
        resetBatchLine() {
            this.tempBatch.beginAtAM = null
            this.tempBatch.endAtAM = null
            this.tempBatch.beginAtPM = null
            this.tempBatch.endAtPM = null
        },
        selectSchedule() {
            if (this.schedule) {
                this.tempBatch.beginAtAM = this.schedule.from_am
                this.tempBatch.endAtAM = this.schedule.to_am

                if (this.schedule.from_pm.length && this.schedule.to_pm.length) {
                    this.tempBatch.beginAtPM = this.schedule.from_pm
                    this.tempBatch.endAtPM = this.schedule.to_pm
                }

                this.tempBatch.weekdays = range(1, 8).reduce((acc, day) => {
                    acc[day] = Boolean(this.schedule.weekdays[day])
                    return acc
                }, {})
            } else {
                this.resetBatchLine()
            }
        },
        setTempBatchTo() {
            this.tempBatch.to = this.tempBatch.from
        },
        sortTimetable() {
            this.timetable.sort((a, b) => {
                return a.from - b.from
            })
        },
        splitAtWeekBoundary(from, to) {
            const segments = []
            let segmentStart = from

            while (segmentStart < to) {
                const nextWeekStart = segmentStart.startOf('week').plus({ weeks: 1 })
                const segmentEnd = nextWeekStart < to ? nextWeekStart : to

                segments.push({ from: segmentStart, to: segmentEnd })
                segmentStart = segmentEnd
            }

            return segments
        },
        splitLineByWeek(line) {
            return this.splitAtWeekBoundary(line.from, line.to).map(segment => ({
                weekNumber: segment.from.toFormat('WW'),
                duration: segment.to.diff(segment.from, ['hours', 'minutes']),
            }))
        },
        weeksClosingAt(index) {
            const weeksOfLine = this.splitLineByWeek(this.timetable[index]).map(segment => segment.weekNumber)

            return weeksOfLine.filter(weekNumber => {
                return this.timetable.every((otherLine, otherIndex) => {
                    if (otherIndex <= index) {
                        return true
                    }

                    return this.splitLineByWeek(otherLine).every(segment => segment.weekNumber !== weekNumber)
                })
            })
        },
    },
}
</script>

<template>
    <div>
        <form v-if="!disabled" @submit.prevent="batchAddLines()">
            <div class="row mb-3">
                <div v-if="schedules.length" class="col-12">
                    <div class="form-inline">
                        <label for="Schedule">Horaire type :</label>
                        <select id="Schedule" v-model="schedule" class="form-control ml-3" @change="selectSchedule">
                            <option></option>
                            <option v-for="sched in schedules" :key="sched.id" :value="sched">{{ sched.name }}</option>
                        </select>
                    </div>
                </div>
                <div class="col-12">
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:my-4 tw:gap-x-5 tw:gap-y-2">
                        <label class="tw:mb-0">Jours travaillés : </label>
                        <div v-for="(day, dayKey) in weekdays" :key="dayKey" class="form-check">
                            <input
                                :id="'Weekday-' + dayKey"
                                v-model="tempBatch.weekdays[dayKey]"
                                type="checkbox"
                                class="form-check-input"
                            />
                            <label class="form-check-label" :for="'Weekday-' + dayKey">{{ day }}</label>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <div class="form-inline">
                        <div class="d-flex m-3">
                            <input id="continuous" v-model="timeSlot" type="radio" value="continuous" class="mr-1" />
                            <label for="continuous">Horaires continus</label>
                        </div>
                        <div class="d-flex m-3">
                            <input
                                id="discontinuous"
                                v-model="timeSlot"
                                type="radio"
                                value="discontinuous"
                                class="mr-1"
                            />
                            <label for="discontinuous">Horaires discontinus</label>
                        </div>
                    </div>
                </div>
                <div class="col-md-6 mb-3" :class="timeSlot === 'discontinuous' ? 'col-xl-2' : 'col-lg-3'">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">Du&nbsp;<span title="Requis">*</span></div>
                        </div>
                        <input
                            v-model="tempBatch.from"
                            type="date"
                            required
                            class="form-control"
                            @change="setTempBatchTo()"
                        />
                    </div>
                    <small v-if="dateError" class="form-text text-danger">{{ dateError }}</small>
                </div>
                <div class="col-md-6 mb-3" :class="timeSlot === 'discontinuous' ? 'col-xl-2' : 'col-lg-3'">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">Au&nbsp;<span title="Requis">*</span></div>
                        </div>
                        <input v-model="tempBatch.to" type="date" required :min="tempBatch.from" class="form-control" />
                    </div>
                </div>
                <div class="col-md-6 mb-3" :class="timeSlot === 'discontinuous' ? 'col-xl-3' : 'col-lg-4'">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">
                                Plage horaire<span v-if="timeSlot == 'discontinuous'">&nbsp;1</span>&nbsp;<span
                                    title="Requis"
                                    >*</span
                                >
                            </div>
                        </div>
                        <input
                            v-model="tempBatch.beginAtAM"
                            type="time"
                            required
                            class="form-control"
                            @change="tempBatch.endAtAM = tempBatch.beginAtAM"
                        />
                        <input v-model="tempBatch.endAtAM" type="time" required class="form-control" />
                    </div>
                    <small v-show="tempBatch.beginAtAM > tempBatch.endAtAM" class="form-text text-muted"
                        ><span class="color-fuel-yellow">Attention</span> : cette plage horaire se terminera le jour
                        suivant</small
                    >
                </div>
                <div v-if="timeSlot == 'discontinuous'" class="col-xl-3 col-md-6 mb-3">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <div class="input-group-text">Plage horaire 2</div>
                        </div>
                        <input
                            v-model="tempBatch.beginAtPM"
                            type="time"
                            class="form-control"
                            @change="tempBatch.endAtPM = tempBatch.beginAtPM"
                        />
                        <input v-model="tempBatch.endAtPM" type="time" class="form-control" />
                    </div>
                    <small v-show="tempBatch.beginAtPM > tempBatch.endAtPM" class="form-text text-muted"
                        ><span class="color-fuel-yellow">Attention</span> : cette plage horaire se terminera le jour
                        suivant</small
                    >
                </div>
                <div class="col-md-2 text-center text-md-left" :class="timeSlot === 'discontinuous' ? 'mx-auto' : ''">
                    <button class="btn p-0 btn-no-spinner" type="submit" :disabled="formIsDisabled">
                        <i title="Ajouter un horaire" class="fa-solid fa-circle-plus fa-2x color-blue-calypso"></i>
                    </button>
                </div>
            </div>
        </form>

        <div class="table-responsive">
            <table class="table table-bordered overflow-auto">
                <thead v-show="!disabled">
                    <tr>
                        <th colspan="5" class="text-right">
                            <button
                                type="button"
                                class="btn btn-blue-calypso"
                                :class="{ disabled: !timetable.length }"
                                @click.prevent="dropAllLines"
                            >
                                Tout supprimer
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(line, index) in timetable" :key="index">
                        <planning-line-component
                            :line="line"
                            :disabled="disabled"
                            @line-updated="onLineUpdated"
                            @drop-line="dropLine(index)"
                            @duplicate-to-next-day="duplicateToNextDay(index)"
                        ></planning-line-component>
                        <tr
                            v-for="weekNumber in weeksClosingAt(index)"
                            :key="weekNumber"
                            class="bg-fuel-yellow--lighter"
                        >
                            <td>Semaine {{ weekNumber }}</td>
                            <td colspan="4">
                                <span v-text="planningDurationPerWeek[weekNumber].hours" />h<span
                                    v-text="String(planningDurationPerWeek[weekNumber].minutes).padStart(2, '0')"
                                />m
                            </td>
                        </tr>
                    </template>
                    <tr v-if="timetable.length == 0">
                        <td></td>
                        <td class="text-center" colspan="4">Aucun horaire renseigné</td>
                    </tr>
                    <tr>
                        <td><strong>Total</strong></td>
                        <td colspan="5">
                            <span v-text="planningDuration.hours" />h<span
                                v-text="String(planningDuration.minutes).padStart(2, '0')"
                            />m
                        </td>
                    </tr>
                </tbody>
            </table>
            <input type="hidden" :name="getInputName()" :value="JSON.stringify(mergedTimetable)" />
            <input type="hidden" name="begin_at_date" :value="timetable.length ? timetable[0].from.toISODate() : ''" />
            <input
                type="hidden"
                name="begin_at_time"
                :value="timetable.length ? timetable[0].from.toFormat('HH:mm') : ''"
            />
        </div>
    </div>
</template>

<style lang="scss">
.w-10 {
    width: 10%;
}

input[type='radio'] {
    accent-color: #148181;
}
</style>
